import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiResource } from '../utils/useApiResource';

export interface MyAllocationWindow {
  id: string;
  /** Formatted date range, e.g. "1 Jun – 30 Jun" or "From 1 Jun" (open-ended). */
  label: string;
  percentage: number;
}

export interface MyAllocationGroup {
  projectId: string;
  projectName: string;
  companyName: string | null;
  color: string | null;
  /** One per allocation window, chronological — a project can recur over time. */
  windows: MyAllocationWindow[];
}

interface RawAllocation {
  id: string;
  percentage: number;
  startDate: string;
  endDate: string;
  status: string;
  project: {
    id: string;
    name: string;
    color: string | null;
    company: { name: string } | null;
  } | null;
}

const DAY_MS = 86_400_000;
// An end date this far out reads as open-ended ("From {start}") rather than a range.
const OPEN_ENDED_DAYS = 365;

/** Monday 00:00 of the week containing `d`. */
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

function formatWindow(startISO: string, endISO: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const start = new Date(startISO);
  const startStr = start.toLocaleDateString('en-GB', opts);
  const end = new Date(endISO);
  if ((end.getTime() - Date.now()) / DAY_MS > OPEN_ENDED_DAYS) return `From ${startStr}`;
  return `${startStr} – ${end.toLocaleDateString('en-GB', opts)}`;
}

interface UseMyAllocationsResult {
  groups: MyAllocationGroup[];
  loading: boolean;
  error: string | null;
}

/**
 * The signed-in user's project allocations overlapping the current week, grouped
 * by project so a project that recurs over several windows reads as one entry
 * with its date ranges listed beneath. Own-data via the linked employee id
 * (surfaced on the profile by the M3 change); empty when the user isn't linked.
 * `enabled` gates the fetch so callers only load when shown. The
 * `/allocations/employee/:id` endpoint masks financial fields (value / margin /
 * roi) — `percentage` is not financial and is safe to display.
 */
export function useMyAllocations(enabled: boolean): UseMyAllocationsResult {
  const { user } = useAuth();
  const employeeId = user?.employeeId ?? null;

  const { data, loading, error } = useApiResource<RawAllocation[]>(
    (client) => client.get<RawAllocation[]>(`/allocations/employee/${employeeId}`),
    [employeeId, enabled],
    { skip: !enabled || !employeeId },
  );

  const groups = useMemo<MyAllocationGroup[]>(() => {
    if (!data) return [];
    const weekStart = startOfWeek(new Date());
    const weekEnd = new Date(weekStart.getTime() + 7 * DAY_MS - 1);

    // Chronological, so each project's windows come out in date order.
    const sorted = [...data].sort((a, b) => a.startDate.localeCompare(b.startDate));

    const byProject = new Map<string, MyAllocationGroup>();
    for (const a of sorted) {
      if (!a.project) continue;
      if (new Date(a.startDate) > weekEnd || new Date(a.endDate) < weekStart) continue;
      let group = byProject.get(a.project.id);
      if (!group) {
        group = {
          projectId: a.project.id,
          projectName: a.project.name,
          companyName: a.project.company?.name ?? null,
          color: a.project.color,
          windows: [],
        };
        byProject.set(a.project.id, group);
      }
      group.windows.push({ id: a.id, label: formatWindow(a.startDate, a.endDate), percentage: a.percentage });
    }

    // Most-allocated project first (by its highest window).
    return [...byProject.values()].sort(
      (x, y) =>
        Math.max(...y.windows.map((w) => w.percentage)) -
        Math.max(...x.windows.map((w) => w.percentage)),
    );
  }, [data]);

  return { groups, loading, error };
}
